'use client'

import { useCallback, useMemo } from 'react'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { RawHtmlSectionFragmentFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useLocalization } from '../../../../_providers/LocalizationProvider.client'

export const rawHtmlQueryFragment = gql(`
  fragment RawHtmlSectionFragment on RawHtmlSection {
    __typename
    id
    html
    hasVerticalMargin
  }
`)

/**
 * Expands `{{token}}` placeholders in CMS-authored HTML. Token names are matched
 * case-insensitively and tolerate surrounding whitespace, since the content is hand-authored.
 * Unknown tokens are left in place so a typo is visible in the DOM rather than silent.
 *
 * Currently only used for "localizing" FriendBuy content on our referral pages by generating an `id`
 * on a `div` that allows FriendBuy to place content based on a "localized" `div`.
 */
const expandTokens = (rawHtml: string, tokens: Record<string, string>) => {
  if (rawHtml) {
    const lookup = new Map(Object.entries(tokens).map(([key, value]) => [key.toLowerCase(), value]))

    return rawHtml.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (match, token: string) => lookup.get(token.toLowerCase()) ?? match
    )
  }
  return rawHtml
}

const extractScripts = (rawHtml: string) => {
  let html = rawHtml
  const scriptTexts: string[] = []

  if (html) {
    // Regex + HTML https://stackoverflow.com/a/1732454/161182
    const tags = html.match(/<script[^>]*>[\s\S]*?<\/script>/g)
    if (tags) {
      tags.forEach(tag => {
        const text = tag.match(/<script[^>]*>([\s\S]*)<\/script>/)![1]
        if (text) scriptTexts.push(text)
        html = html.replace(tag, '')
      })
    }
  }

  return {
    htmlWithoutScripts: html,
    scriptTexts,
  }
}

// Simple synchronous string hash (no crypto APIs) so this works identically during SSR and in the browser
const hashScriptText = (text: string): string => {
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0
  }
  return hash.toString(36)
}

const embeddedScriptDataAttr = 'data-ctf-raw-html-script-hash'

type ContentfulRawHtmlProps = Omit<RawHtmlSectionFragmentFragment, '__typename' | 'id'> & {
  className?: string
}

export const ContentfulRawHtml = ({ html, className }: ContentfulRawHtmlProps) => {
  const { languageGroup, region } = useLocalization()
  /**
   * Currently only used for "localizing" FriendBuy content on our referral pages by generating an `id`
   * on a `div` that allows FriendBuy to place content based on a "localized" `div`.
   */
  const shopLocale = `${languageGroup}-${region.id}`.toLowerCase()

  const { htmlWithoutScripts, scriptTexts } = useMemo(
    () => extractScripts(expandTokens(html, { shopLocale })),
    [html, shopLocale]
  )

  const loadEmbeddedScriptsCallbackRef = useCallback(() => {
    // Skip scripts that have already executed once this session to avoid duplicate-declaration errors
    const pendingScripts = scriptTexts
      .map(text => ({ text, hash: hashScriptText(text) }))
      .filter(({ hash }) => !document.querySelector(`script[${embeddedScriptDataAttr}="${hash}"]`))

    // Create script nodes in memory for each extracted script tag that hasn't run yet
    const nodes = pendingScripts.map(({ text, hash }) => {
      const node = document.createElement('script')
      node.innerText = text
      node.setAttribute(embeddedScriptDataAttr, hash)
      return node
    })

    // Append each script node to the body
    for (const node of nodes) {
      document.body.appendChild(node)
    }
  }, [scriptTexts])

  return (
    <>
      <div
        ref={loadEmbeddedScriptsCallbackRef}
        className={className}
        dangerouslySetInnerHTML={{ __html: htmlWithoutScripts }}
      />
    </>
  )
}
